<template>
  <div class="quick-chart-container">
    <div class="chart-controls">
      <b-row class="mb-3">
        <b-col md="4">
          <label class="form-label">Start Time</label>
          <b-form-input
            v-model="chartParams.startTime"
            type="datetime-local"
            @change="updateChart"
          />
        </b-col>
        <b-col md="4">
          <label class="form-label">End Time</label>
          <b-form-input
            v-model="chartParams.endTime"
            type="datetime-local"
            @change="updateChart"
          />
        </b-col>
        <b-col md="4">
          <label class="form-label">Granularity</label>
          <b-form-select
            v-model="chartParams.granularity"
            :options="granularityOptions"
            @change="updateChart"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-button
            variant="primary"
            :disabled="isLoading"
            @click="refreshChart"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              class="mr-1"
            />
            {{ isLoading ? 'Loading...' : 'Refresh Chart' }}
          </b-button>
        </b-col>
      </b-row>
    </div>

    <div class="chart-container">
      <div
        v-if="isLoading"
        class="loading-spinner"
      >
        <b-spinner variant="primary" />
        <p class="mt-2">
          Loading chart data...
        </p>
      </div>
      <div
        v-if="!isLoading && !chartError && chartData && chartData.length > 0"
        class="chart-wrapper"
      >
        <canvas
          ref="chartCanvas"
          class="chart-canvas"
          @mousemove="onChartMouseMove"
          @mouseleave="onChartMouseLeave"
        />
        <!-- Click Details -->
        <div
          v-if="tooltip.visible"
          class="chart-tooltip"
          :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
        >
          <div class="tooltip-content">
            <div class="tooltip-item">
              <strong>Endpoint</strong>
              <span>{{ tooltip.data.endpoint }}</span>
            </div>
            <div class="tooltip-item">
              <strong>Status</strong>
              <span :class="getStatusClass(tooltip.data.statusCode)">
                {{ tooltip.data.statusCode }} - {{ getStatusText(tooltip.data.statusCode) }}
              </span>
            </div>
            <div class="tooltip-item">
              <strong>Calls</strong>
              <span>{{ tooltip.data.apiCallCount }}</span>
            </div>
            <div class="tooltip-item">
              <strong>Time</strong>
              <span>{{ tooltip.data.timeLabel }}</span>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="chartError"
        class="error-message"
      >
        <b-alert
          variant="danger"
          show
        >
          <h4 class="alert-heading">
            Chart Error
          </h4>
          <p>{{ chartError }}</p>
          <b-button
            variant="outline-danger"
            @click="retryChart"
          >
            Retry
          </b-button>
        </b-alert>
      </div>

      <!-- No Data Message -->
      <div
        v-if="!isLoading && !chartError && (!chartData || chartData.length === 0)"
        class="no-data-message"
      >
        <b-alert
          variant="info"
          show
        >
          <h4 class="alert-heading">
            No Data Available
          </h4>
          <p>No data points found for the selected time range. Please adjust your filters and try again.</p>
        </b-alert>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BButton, BRow, BCol, BFormInput, BFormSelect, BSpinner, BAlert,
} from 'bootstrap-vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import { apiToastError } from '@/@core/utils/toast'
import { getStatusText, getStatusClass } from '@/utils/statusUtils'

export default {
  name: 'IntegrationMetrics',
  components: {
    BButton,
    BRow,
    BCol,
    BFormInput,
    BFormSelect,
    BSpinner,
    BAlert,
    FeatherIcon,
  },
  data() {
    return {
      isLoading: false,
      chartError: null,
      chartData: [],
      tooltip: {
        visible: false,
        x: 0,
        y: 0,
        data: null,
      },
      granularityOptions: [
        { value: '30 second', text: '30 Second' },
        { value: '1 minute', text: '1 Minute' },
        { value: '5 minute', text: '5 Minute' },
        { value: '10 minute', text: '10 Minute' },
        { value: '15 minute', text: '15 Minute' },
        { value: '30 minute', text: '30 Minute' },
        { value: '1 hour', text: '1 Hour' },
        { value: '2 hour', text: '2 Hour' },
        { value: '6 hour', text: '6 Hour' },
        { value: '12 hour', text: '12 Hour' },
        { value: '1 day', text: '1 Day' },
        { value: '2 day', text: '2 Day' },
        { value: '7 day', text: '7 Day' },
        { value: '30 day', text: '30 Day' },
      ],
      chartParams: {
        startTime: this.formatDateTimeForInput(new Date(Date.now() - 24 * 60 * 60 * 1000)), // 24 hours ago
        endTime: this.formatDateTimeForInput(new Date()), // now
        granularity: '1 hour',
      },
    }
  },
  mounted() {
    this.initializeChart()
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy()
    }
  },
  methods: {
    formatDateTimeForInput(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day}T${hours}:${minutes}`
    },

    formatDateTimeForAPI(dateString) {
      return new Date(dateString).getTime()
    },

    async initializeChart() {
      await this.fetchChartData()
    },

    async fetchChartData() {
      this.isLoading = true
      this.chartError = null

      try {
        const payload = {
          startTime: this.formatDateTimeForAPI(this.chartParams.startTime),
          endTime: this.formatDateTimeForAPI(this.chartParams.endTime),
          granularity: this.chartParams.granularity,
          where: 'auth.entity_id:64876f024d1613ad8d1d18f2',
          limit: 1000,
        }

        const response = await this.$store.dispatch('fetchHyperDxData', payload)

        if (response && response.data) {
          this.parseHyperDxData(response.data)
          this.$nextTick(() => {
            setTimeout(() => {
              this.renderChart()
            }, 100)
          })
        } else {
          throw new Error('No data received from API')
        }
      } catch (error) {
        this.chartError = error.message || 'Failed to load chart data'
        apiToastError('Failed to load chart data. Please try again.')
      } finally {
        this.isLoading = false
      }
    },

    parseHyperDxData(apiResponse) {
      const rawData = this.extractRawData(apiResponse)
      if (!rawData) {
        this.chartData = []
        return
      }

      const filteredData = this.filterValidData(rawData)
      this.chartData = this.transformDataToChartFormat(filteredData)
    },

    extractRawData(apiResponse) {
      if (Array.isArray(apiResponse)) {
        return apiResponse
      }
      if (apiResponse.data && Array.isArray(apiResponse.data)) {
        return apiResponse.data
      }
      if (apiResponse.results && Array.isArray(apiResponse.results)) {
        return apiResponse.results
      }
      return null
    },

    filterValidData(rawData) {
      if (!Array.isArray(rawData) || rawData.length === 0) {
        return []
      }
      return rawData.filter(item => item.group && item.group.length !== 0)
    },

    transformDataToChartFormat(filteredData) {
      return filteredData.map(item => this.transformDataItem(item))
        .sort((a, b) => a.timestamp - b.timestamp)
    },

    transformDataItem(item) {
      const timestamp = item.ts_bucket
      const apiCallCount = item['series_0.data'] || 0
      const group = item.group || []
      const endpoint = group[1] || 'unknown'
      const statusCode = group[2] || '0'
      const method = group[3] || 'unknown'
      const date = new Date(timestamp)

      return {
        timestamp,
        value: apiCallCount,
        endpoint,
        statusCode,
        method,
        apiCallCount,
        timeLabel: date.toLocaleTimeString(),
      }
    },

    renderChart() {
      if (!this.chartData || this.chartData.length === 0) {
        return
      }

      const canvas = this.$refs.chartCanvas
      if (!canvas) {
        return
      }

      const ctx = canvas.getContext('2d')
      this.setupCanvas(canvas, ctx)
      this.prepareChartData()
      this.drawChartBackground(ctx)
      this.drawChartLine(ctx)
      this.drawDataPoints(ctx)
      this.drawLabels(ctx)
      this.drawTitles(ctx)
    },

    setupCanvas(canvas, ctx) {
      canvas.width = 800
      canvas.height = 400
      ctx.clearRect(0, 0, canvas.width, canvas.height)
    },

    prepareChartData() {
      const values = this.chartData.map(item => item.value)
      const maxValue = Math.max(...values)
      const minValue = Math.min(...values)
      const valueRange = maxValue - minValue

      if (valueRange === 0) {
        this.adjustDataForSingleValue(minValue)
      } else {
        this.setDataRange(minValue, maxValue)
      }
    },

    adjustDataForSingleValue(minValue) {
      const centerValue = minValue
      const step = Math.max(1, Math.ceil(centerValue * 0.2))
      const adjustedMinValue = Math.max(0, centerValue - 2 * step)
      const adjustedMaxValue = centerValue + 2 * step

      this.chartData.forEach(item => {
        item.adjustedValue = item.value
        item.adjustedMinValue = adjustedMinValue
        item.adjustedMaxValue = adjustedMaxValue
      })
    },

    setDataRange(minValue, maxValue) {
      this.chartData.forEach(item => {
        item.adjustedValue = item.value
        item.adjustedMinValue = minValue
        item.adjustedMaxValue = maxValue
      })
    },

    drawChartBackground(ctx) {
      const padding = 60
      const width = ctx.canvas.width
      const height = ctx.canvas.height
      const chartHeight = height - 2 * padding

      // Draw axes
      ctx.strokeStyle = '#ddd'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(padding, padding)
      ctx.lineTo(padding, height - padding)
      ctx.lineTo(width - padding, height - padding)
      ctx.stroke()

      // Draw grid lines
      ctx.strokeStyle = '#f0f0f0'
      ctx.lineWidth = 0.5
      for (let i = 0; i <= 5; i++) {
        const y = padding + (chartHeight / 5) * i
        ctx.beginPath()
        ctx.moveTo(padding, y)
        ctx.lineTo(width - padding, y)
        ctx.stroke()
      }
    },

    drawChartLine(ctx) {
      if (this.chartData.length <= 1) return

      const padding = 60
      const width = ctx.canvas.width
      const height = ctx.canvas.height
      const chartWidth = width - 2 * padding
      const chartHeight = height - 2 * padding

      ctx.strokeStyle = '#007bff'
      ctx.lineWidth = 3
      ctx.beginPath()

      this.chartData.forEach((item, index) => {
        const x = padding + (index / (this.chartData.length - 1)) * chartWidth
        const y = height - padding - ((item.adjustedValue - item.adjustedMinValue) / (item.adjustedMaxValue - item.adjustedMinValue)) * chartHeight

        if (index === 0) {
          ctx.moveTo(x, y)
        } else {
          ctx.lineTo(x, y)
        }
      })
      ctx.stroke()
    },

    drawDataPoints(ctx) {
      const padding = 60
      const width = ctx.canvas.width
      const height = ctx.canvas.height
      const chartWidth = width - 2 * padding
      const chartHeight = height - 2 * padding

      this.chartData.forEach((item, index) => {
        const x = this.chartData.length === 1
          ? padding + chartWidth / 2
          : padding + (index / (this.chartData.length - 1)) * chartWidth
        const y = height - padding - ((item.adjustedValue - item.adjustedMinValue) / (item.adjustedMaxValue - item.adjustedMinValue)) * chartHeight

        this.drawDataPoint(ctx, x, y)
      })
    },

    drawDataPoint(ctx, x, y) {
      // Draw point with multiple layers for visual effect
      const layers = [
        { color: 'rgba(0, 123, 255, 0.1)', radius: 15 },
        { color: 'rgba(0, 123, 255, 0.3)', radius: 10 },
        { color: '#007bff', radius: 6 },
        { color: '#ffffff', radius: 3 },
      ]

      layers.forEach(layer => {
        ctx.fillStyle = layer.color
        ctx.beginPath()
        ctx.arc(x, y, layer.radius, 0, 2 * Math.PI)
        ctx.fill()
      })
    },

    drawLabels(ctx) {
      const padding = 60
      const width = ctx.canvas.width
      const height = ctx.canvas.height
      const chartWidth = width - 2 * padding
      const chartHeight = height - 2 * padding

      ctx.fillStyle = '#333'
      ctx.font = '12px Arial'
      ctx.textAlign = 'center'

      this.drawYAxisLabels(ctx, padding, chartHeight)
      this.drawXAxisLabels(ctx, padding, chartWidth, height)
    },

    drawYAxisLabels(ctx, padding, chartHeight) {
      for (let i = 0; i <= 5; i++) {
        const adjustedMinValue = this.chartData[0].adjustedMinValue
        const adjustedMaxValue = this.chartData[0].adjustedMaxValue
        const adjustedRange = adjustedMaxValue - adjustedMinValue
        const value = adjustedMinValue + (adjustedRange / 5) * (5 - i)
        const y = padding + (chartHeight / 5) * i
        ctx.fillText(Math.round(value).toLocaleString(), padding - 10, y + 4)
      }
    },

    drawXAxisLabels(ctx, padding, chartWidth, height) {
      if (this.chartData.length === 1) {
        this.drawSingleDataPointLabel(ctx, padding, chartWidth, height)
      } else {
        this.drawMultipleDataPointLabels(ctx, padding, chartWidth, height)
      }
    },

    drawSingleDataPointLabel(ctx, padding, chartWidth, height) {
      const dataPoint = this.chartData[0]
      if (!dataPoint?.timestamp) return

      const date = new Date(dataPoint.timestamp)
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
      const x = padding + chartWidth / 2
      ctx.fillText(dateStr, x, height - padding + 15)
      ctx.fillText(timeStr, x, height - padding + 30)
    },

    drawMultipleDataPointLabels(ctx, padding, chartWidth, height) {
      const maxLabels = Math.min(8, this.chartData.length)
      for (let i = 0; i < maxLabels; i++) {
        const index = Math.floor((this.chartData.length - 1) * i / (maxLabels - 1))
        const x = padding + (index / (this.chartData.length - 1)) * chartWidth
        const dataPoint = this.chartData[index]

        if (dataPoint?.timestamp) {
          const date = new Date(dataPoint.timestamp)
          const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          ctx.fillText(dateStr, x, height - padding + 15)
          ctx.fillText(timeStr, x, height - padding + 30)
        }
      }
    },

    drawTitles(ctx) {
      const width = ctx.canvas.width
      const height = ctx.canvas.height

      // Chart title
      ctx.font = 'bold 16px Arial'
      ctx.textAlign = 'center'
      ctx.fillText('API Call Metrics - Entity Service', width / 2, 25)

      // Y-axis title
      ctx.save()
      ctx.translate(15, height / 2)
      ctx.rotate(-Math.PI / 2)
      ctx.font = '12px Arial'
      ctx.fillText('API Calls Count', 0, 0)
      ctx.restore()

      // X-axis title
      ctx.font = '12px Arial'
      ctx.textAlign = 'center'
      ctx.fillText('Time', width / 2, height - 10)
    },

    updateChart() {
      // Debounce the chart updates to avoid too many API calls
      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout)
      }

      this.updateTimeout = setTimeout(() => {
        this.fetchChartData()
      }, 500)
    },

    refreshChart() {
      this.fetchChartData()
    },

    retryChart() {
      this.fetchChartData()
    },

    onChartMouseMove(event) {
      if (!this.chartData || this.chartData.length === 0) return

      const canvas = this.$refs.chartCanvas
      if (!canvas) return

      const mousePosition = this.getMousePosition(event, canvas)
      const closestPoint = this.findClosestDataPoint(mousePosition, canvas)

      if (closestPoint) {
        this.showTooltip(closestPoint, mousePosition, canvas)
      } else {
        this.hideTooltip()
      }
    },

    getMousePosition(event, canvas) {
      const rect = canvas.getBoundingClientRect()
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      }
    },

    findClosestDataPoint(mousePosition, canvas) {
      const chartDimensions = this.getChartDimensions(canvas)
      const valueRange = this.calculateValueRange()
      let closestPoint = null
      let minDistance = Infinity

      this.chartData.forEach((dataPoint, index) => {
        const pointPosition = this.calculateDataPointPosition(index, dataPoint, chartDimensions, valueRange)
        const distance = this.calculateDistance(mousePosition, pointPosition)

        if (distance < minDistance && distance < 200) {
          minDistance = distance
          closestPoint = dataPoint
        }
      })

      return closestPoint
    },

    getChartDimensions(canvas) {
      const padding = 60
      return {
        padding,
        width: canvas.width - 2 * padding,
        height: canvas.height - 2 * padding,
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
      }
    },

    calculateValueRange() {
      const values = this.chartData.map(item => item.value)
      const minValue = Math.min(...values)
      const maxValue = Math.max(...values)
      return maxValue - minValue || 1
    },

    calculateDataPointPosition(index, dataPoint, chartDimensions, valueRange) {
      const {
        padding,
        width,
        height,
        canvasHeight,
      } = chartDimensions
      const x = padding + (index / (this.chartData.length - 1)) * width
      const y = canvasHeight - padding - ((dataPoint.value - Math.min(...this.chartData.map(item => item.value))) / valueRange) * height
      return { x, y }
    },

    calculateDistance(point1, point2) {
      return Math.sqrt((point1.x - point2.x) ** 2 + (point1.y - point2.y) ** 2)
    },

    showTooltip(dataPoint, mousePosition, canvas) {
      this.tooltip.visible = true
      const tooltipPosition = this.calculateTooltipPosition(mousePosition, canvas)
      this.tooltip.x = tooltipPosition.x
      this.tooltip.y = tooltipPosition.y
      this.tooltip.data = dataPoint
    },

    calculateTooltipPosition(mousePosition, canvas) {
      const tooltipWidth = 200
      const tooltipHeight = 120
      let x = mousePosition.x + 10
      let y = mousePosition.y - 10

      // Adjust if tooltip would go off the right edge
      if (x + tooltipWidth > canvas.width) {
        x = mousePosition.x - tooltipWidth - 10
      }

      // Adjust if tooltip would go off the top edge
      if (y - tooltipHeight < 0) {
        y = mousePosition.y + 10
      }

      return { x, y }
    },

    hideTooltip() {
      this.tooltip.visible = false
    },

    onChartMouseLeave() {
      this.tooltip.visible = false
    },

    getStatusText,
    getStatusClass,
  },
}
</script>

<style lang="scss" scoped>
.quick-chart-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.chart-controls {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.form-label {
  font-weight: 600;
  margin-bottom: 8px;
  color: #495057;
}

.chart-container {
  position: relative;
  min-height: 500px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
}

.chart-wrapper {
  position: relative;
  height: 500px;
  padding: 20px;
}

.chart-canvas {
  width: 100% !important;
  height: 100% !important;
  cursor: pointer;
  transition: all 0.2s ease;
}

.chart-canvas:hover {
  transform: scale(1.01);
}

.chart-tooltip {
  position: absolute;
  background: rgba(0, 0, 0, 0.95);
  color: white;
  padding: 8px;
  border-radius: 6px;
  font-size: 11px;
  pointer-events: none;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  max-width: 200px;
  min-width: 150px;
  white-space: normal;
  word-wrap: break-word;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.tooltip-content {
  line-height: 1.3;
}

.tooltip-title {
  font-weight: bold;
  margin-bottom: 6px;
  color: #007bff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding-bottom: 4px;
  font-size: 11px;
}

.tooltip-item {
  margin-bottom: 4px;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.tooltip-item strong {
  color: #e0e0e0;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 1px;
}

.tooltip-item span {
  color: #ffffff;
  font-size: 10px;
  word-break: break-all;
  padding: 1px 0;
}

.status-success {
  color: #28a745;
  font-weight: bold;
}

.status-warning {
  color: #ffc107;
  font-weight: bold;
}

.status-error {
  color: #dc3545;
  font-weight: bold;
}

.status-unknown {
  color: #6c757d;
  font-weight: bold;
}

.loading-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 10;
}

.error-message,
.no-data-message {
  padding: 20px;
  text-align: center;
}

.alert-heading {
  font-size: 1.1rem;
  margin-bottom: 10px;
}

// Responsive design
@media (max-width: 768px) {
  .quick-chart-container {
    padding: 10px;
  }

  .chart-controls {
    padding: 15px;
  }

  .chart-wrapper {
    height: 400px;
    padding: 15px;
  }
}
</style>
